import * as React from "react";
import { cn } from "./cn";
import { ImageLightbox } from "./image-lightbox";

export interface LogoUploaderLabels {
  /** CTA when no logo is set, e.g. "Tải logo lên". */
  upload: string;
  /** CTA when a logo already exists, e.g. "Đổi logo". */
  change: string;
  /** Remove button, e.g. "Xoá logo". */
  remove: string;
  /** Shown inline when an upload fails. */
  uploadError: string;
  /** Optional helper text under the control. */
  hint?: string;
  /** Aria-label for opening the logo preview. Defaults to "Xem ảnh". */
  view?: string;
  /** Aria-label for the lightbox close button. Defaults to "Đóng". */
  close?: string;
}

export interface LogoUploaderProps {
  /** Current logo URL, or null when unset. */
  url: string | null;
  /**
   * Presign + PUT one file, resolve to the new logo. Thrown errors surface
   * inline. `onProgress` receives the upload percent (0–100) — forward it to
   * the upload hook to drive the percent overlay.
   */
  onUpload: (
    file: File,
    onProgress: (percent: number) => void,
  ) => Promise<{ key: string; url: string }>;
  /** Persist the new key after a successful upload. */
  onChange: (next: { key: string; url: string }) => void | Promise<void>;
  /** Clear the logo. Omit to hide the remove button. */
  onRemove?: () => void | Promise<void>;
  labels: LogoUploaderLabels;
  /** Disable all editing (e.g. non-owner). */
  disabled?: boolean;
  /** Accepted MIME types for the file picker. */
  accept?: string;
  className?: string;
}

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/webp";

// Presentational single-image uploader for the org logo ("avatar"). No data
// fetching — the caller injects `onUpload` (presign + PUT) and persists via
// `onChange` / `onRemove`. Mirrors ImageGalleryEditor's contract so the two
// read the same on the employer + admin screens.
export function LogoUploader({
  url,
  onUpload,
  onChange,
  onRemove,
  labels,
  disabled = false,
  accept = DEFAULT_ACCEPT,
  className,
}: LogoUploaderProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState(false);

  async function handleFiles(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setError(false);
    setProgress(0);
    setBusy(true);
    try {
      const next = await onUpload(file, setProgress);
      await onChange(next);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleRemove() {
    if (!onRemove) return;
    setError(false);
    setBusy(true);
    try {
      await onRemove();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-smoke bg-mist">
          {url ? (
            <button
              type="button"
              aria-label={labels.view ?? "Xem ảnh"}
              onClick={() => setPreview(true)}
              className="block h-full w-full cursor-zoom-in"
            >
              <img
                src={url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink/30">
              <ImageIcon />
            </div>
          )}
          {busy ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/60 px-3">
              <span className="text-sm font-bold tabular-nums text-paper">
                {progress}%
              </span>
              <span className="h-1 w-full overflow-hidden rounded-full bg-paper/30">
                <span
                  className="block h-full rounded-full bg-lime transition-[width] duration-150"
                  style={{ width: `${progress}%` }}
                />
              </span>
            </div>
          ) : null}
        </div>

        {!disabled ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex h-9 items-center justify-center rounded-full bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-50"
            >
              {url ? labels.change : labels.upload}
            </button>
            {url && onRemove ? (
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={busy}
                className="inline-flex h-9 items-center justify-center rounded-full border border-smoke px-4 text-sm font-semibold text-ink hover:bg-mist disabled:opacity-50"
              >
                {labels.remove}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {error ? (
        <p className="text-xs font-semibold text-danger">
          {labels.uploadError}
        </p>
      ) : null}
      {labels.hint ? (
        <p className="text-xs text-ink/50">{labels.hint}</p>
      ) : null}

      <ImageLightbox
        images={url ? [url] : []}
        index={preview && url ? 0 : null}
        onClose={() => setPreview(false)}
        labels={{ close: labels.close ?? "Đóng" }}
      />
    </div>
  );
}

function ImageIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
      <path
        d="M21 15l-5-5L5 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
